import {
  HHDivider,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Fragment } from 'react';
import { IN_PERSON_NAME_KEY } from '../../../constants/strings/contract/form-constants';
import {
  ContractPackagesType,
  ContractTemplateType,
  ContractType,
  useProductOfferingsQuery,
} from '../../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../utils/bills-utils';
import { isFeeForServicePackageByName } from '../../../utils/contract-utils';
import {
  getApplicableDiscounts,
  IDiscount,
} from '../../../utils/discount-utils';
import { getProductOfferingIdsForPackage } from '../../../utils/scope-utils';
import { TextGrouping } from '../../utils/text-grouping';
import { Milestones } from '../contract-view-components/milestones';
import { SelectedPackageIndications } from '../package/components/form-right-panel';
import { ValidBillingModel } from '../package/custom-package-types';
import { PackageBillableActivityDisplay } from './billable-activity-display';
import { CostShareOptionsDisplay } from './cost-share-options-display';
import {
  BILLING_MODEL_DISPLAY_CONFIG,
  type GridItem,
  type PackageDisplayContext,
  type SectionConfig,
} from './package-detail-config';
import { PackageDiscountSummary } from './package-discount-summary';

interface PackageDetailCardProps {
  productPackage: { key: string; contractPackage: ContractPackagesType };
  contract: ContractType;
  templates: ContractTemplateType[];
  slimDisplay: boolean;
  discounts?: IDiscount[];
}

const renderSection = (
  section: SectionConfig,
  ctx: PackageDisplayContext,
): JSX.Element | null => {
  switch (section.sectionId) {
    case 'billableActivities':
      return (
        <PackageBillableActivityDisplay
          packageBillableActivities={
            ctx.contractPackage.billableActivityTypes ?? []
          }
        />
      );
    case 'discounts': {
      if (isFeeForServicePackageByName(ctx.packageKey)) return null;
      const applicable = getApplicableDiscounts(
        ctx.discounts,
        ctx.contractPackage.packageId,
        ctx.contract.startDate,
        ctx.contract.endDate,
        ctx.productOfferingIds,
      );
      return applicable.length > 0 ? (
        <PackageDiscountSummary discounts={applicable} />
      ) : null;
    }
    case 'milestones':
      return (
        <Milestones
          contractDetails={ctx.contractPackage.rules}
          templateName={
            ctx.templates.find(
              t => t.id === ctx.contractPackage.contractTemplateId,
            )?.name
          }
          slimDisplay={ctx.slimDisplay}
          contractBillableActivities={ctx.contractPackage.billableActivityTypes}
          currency={ctx.contract.currency ?? 'USD'}
        />
      );
    case 'costShareOptions':
      return ctx.contractPackage.costShareOptions ? (
        <CostShareOptionsDisplay
          cadence={ctx.contractPackage.costShareOptions.cadence}
          deductionTiming={ctx.contractPackage.costShareOptions.deductionTiming}
        />
      ) : null;
    default:
      return null;
  }
};

const renderGridItems = (
  items: GridItem[],
  ctx: PackageDisplayContext,
): JSX.Element[] => {
  const elements: JSX.Element[] = [];

  for (const item of items) {
    if (item.kind === 'group') {
      elements.push(
        <HHStack
          direction="column"
          gap={2}
          key={`group-${item.fields[0].label}`}
        >
          {item.fields.map(field => {
            const value = field.getValue(ctx);
            return value != null ? (
              <TextGrouping
                key={field.label}
                label={field.label}
                text={value}
              />
            ) : null;
          })}
        </HHStack>,
      );
    } else if (item.kind === 'text') {
      const value = item.getValue(ctx);
      if (value != null) {
        elements.push(
          <TextGrouping key={item.label} label={item.label} text={value} />,
        );
      }
    } else {
      const rendered = renderSection(item, ctx);
      if (rendered) {
        elements.push(<Fragment key={item.sectionId}>{rendered}</Fragment>);
      }
    }
  }

  return elements;
};

export const PackageDetailCard = ({
  productPackage,
  contract,
  templates,
  slimDisplay,
  discounts,
}: PackageDetailCardProps): JSX.Element => {
  const billingModelName = productPackage.contractPackage.billingModel
    .name as ValidBillingModel;

  const { data: productOfferingsData } = useProductOfferingsQuery();
  const productOfferingIds = getProductOfferingIdsForPackage(
    productPackage.contractPackage.package.packageOfferings,
    productOfferingsData?.productOfferings ?? [],
  );

  if (!Object.values(ValidBillingModel).includes(billingModelName)) {
    return (
      <HHTypography hhVariant="muted">
        {`Invalid billing model for package: ${productPackage.contractPackage.billingModel.name}`}
      </HHTypography>
    );
  }

  const ctx: PackageDisplayContext = {
    contractPackage: productPackage.contractPackage,
    contract,
    packageKey: productPackage.key,
    templates,
    discounts,
    productOfferingIds,
    slimDisplay,
  };

  const config = BILLING_MODEL_DISPLAY_CONFIG[billingModelName];

  return (
    <HHStack gap={4}>
      {/* Package Header */}
      <HHStack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <HHTypography
          hhVariant="body"
          {...({ layoutStyles: { fontWeight: '500' } } as Record<
            string,
            unknown
          >)}
        >
          {`${
            productPackage.key === IN_PERSON_NAME_KEY
              ? capitalizeFirstLetterAndSpaceSnakeString(productPackage.key)
              : productPackage.key
          } package`}
        </HHTypography>
        {productPackage.contractPackage.package.packageOfferings.some(
          p => p.packageOffering === 'program_indication',
        ) && (
          <SelectedPackageIndications
            packageOfferings={
              productPackage.contractPackage.package.packageOfferings
            }
          />
        )}
      </HHStack>

      {/* Package Details Grid */}
      <HHStack
        display="grid"
        gridTemplateColumns={slimDisplay ? '1fr' : 'repeat(4, 1fr)'}
        gap={slimDisplay ? 2 : 0}
      >
        {renderGridItems(config.gridItems, ctx)}
      </HHStack>

      {/* Below-Grid Sections */}
      {config.belowGridSections.map(section => {
        const rendered = renderSection(section, ctx);
        if (!rendered) {
          return null;
        }
        return (
          <Fragment key={section.sectionId}>
            {section.hasDivider && <HHDivider />}
            {rendered}
          </Fragment>
        );
      })}
    </HHStack>
  );
};
